import type { TooltipItem } from 'chart.js';
import { format } from 'date-fns';

import { formatBytes } from '~/shared/utils/formatNumber';

import { dashedLineSwatchHtml } from './chartSwatches';
import { chartColorForLabel, utilizationDatasetLabel } from './helpers';
import type { ChartTypes } from './Legends';

export interface TooltipDataPoint {
  parsed: { x?: number; y?: number | null };
  dataset?: {
    label?: string;
    borderColor?: string | string[];
    borderDash?: number[];
  };
}

const tooltipLabelOrder = [
  'Datafy Used',
  'Used',
  'Size',
  'Original Size',
  utilizationDatasetLabel,
];

const labelSortIndex = (label: string) => {
  const index = tooltipLabelOrder.indexOf(label);
  return index === -1 ? tooltipLabelOrder.length : index;
};

export function mapTooltipModelDataPoints(points: TooltipItem<'line'>[]): TooltipDataPoint[] {
  return points.map((point) => ({
    parsed: {
      x: typeof point.parsed.x === 'number' ? point.parsed.x : undefined,
      y: typeof point.parsed.y === 'number' ? point.parsed.y : null,
    },
    dataset: {
      label: typeof point.dataset.label === 'string' ? point.dataset.label : undefined,
      borderColor:
        typeof point.dataset.borderColor === 'string' ? point.dataset.borderColor : undefined,
      borderDash: Array.isArray(point.dataset.borderDash)
        ? point.dataset.borderDash.filter((value): value is number => typeof value === 'number')
        : undefined,
    },
  }));
}

const valueRowStyle =
  'display: flex; align-items: center; gap: 6px; font-weight: 400; line-height: 20px; font-size: 14px; color: black;';

const dateStyles =
  'font-weight: 700; font-size: 14px; line-height: 24px; color: #949EAC; letter-spacing: -0.4px;';

function swatchHtml(color: string, dashed: boolean): string {
  if (dashed) {
    return dashedLineSwatchHtml(color);
  }
  return `<span style="display: inline-block; width: 12px; height: 4px; background: ${color}; flex-shrink: 0;"></span>`;
}

function formatTooltipValue(label: string, y: number): string {
  if (label === utilizationDatasetLabel || label.toLowerCase().includes('utilization')) {
    return `${y.toFixed(1)}%`;
  }
  return formatBytes(y, 1);
}

function resolveSwatchColor(label: string, borderColor?: string | string[]): string {
  if (typeof borderColor === 'string') {
    return borderColor;
  }
  return chartColorForLabel(label);
}

function buildDateBlock(timestamp: number | undefined): string {
  const dateObj = timestamp ? new Date(timestamp) : new Date();
  const timeString = format(dateObj, 'hh:mm a');
  const dateStringFormatted = format(dateObj, 'dd MMM');

  return (
    `<div style="display: flex; flex-direction: column; align-items: center">` +
    `<div style="${dateStyles}">${timeString}</div>` +
    `<div style="${dateStyles}">${dateStringFormatted}</div></div>`
  );
}

export function buildTooltipHtml({
  dataPoints,
  chartType,
}: {
  dataPoints: TooltipDataPoint[];
  chartType: ChartTypes;
}): string {
  const sortedPoints = [...dataPoints]
    .filter((point) => {
      const y = point.parsed?.y;
      const label = point.dataset?.label ?? '';
      return typeof y === 'number' && !Number.isNaN(y) && label.length > 0;
    })
    .sort(
      (a, b) =>
        labelSortIndex(a.dataset?.label ?? '') - labelSortIndex(b.dataset?.label ?? '')
    );

  if (sortedPoints.length === 0) {
    return '';
  }

  const timestamp = sortedPoints[0].parsed?.x;

  if (chartType === 'utilization') {
    const utilizationPoint = sortedPoints.find(
      (point) => point.dataset?.label === utilizationDatasetLabel
    );
    if (!utilizationPoint) {
      return '';
    }

    const y = utilizationPoint.parsed.y as number;
    const utilizationTimestamp = utilizationPoint.parsed?.x;
    const singleValueStyle =
      'font-weight: 400; line-height: 20px; font-size: 14px; color: black;';
    return `<div style="${singleValueStyle}">${y.toFixed(1)}%</div>${buildDateBlock(utilizationTimestamp)}`;
  }

  const rowsHtml = sortedPoints
    .map((point) => {
      const label = point.dataset?.label ?? '';
      const y = point.parsed.y as number;
      const color = resolveSwatchColor(label, point.dataset?.borderColor);
      const dashed = label === 'Original Size' || (point.dataset?.borderDash?.length ?? 0) > 0;
      return `<div style="${valueRowStyle}">${swatchHtml(color, dashed)}<span>${formatTooltipValue(label, y)}</span></div>`;
    })
    .join('');

  return (
    `<div style="display: flex; flex-direction: column; gap: 4px; width: 100%;">${rowsHtml}</div>` +
    buildDateBlock(timestamp)
  );
}
